import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Image,
  Input,
  InputNumber,
  Message,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Tabs,
  Tag,
  Upload
} from '@arco-design/web-react';
import { IconCheck, IconDelete, IconEdit, IconPlus } from '@arco-design/web-react/icon';
import dayjs from 'dayjs';

import { get, post } from '../requestNew';
import { API_CONFIG } from '../utils';
import './index.scss';

const FormItem = Form.Item;
const Option = Select.Option;
const URL_UPLOAD = API_CONFIG.uploadUrl;

const STATUS_BACKUP = 0;
const STATUS_ACTIVE = 1;

/** 赛季无分组配置时的默认 Tab（与后台 GroupTypeConstants 一致） */
const DEFAULT_GROUP_TABS = [
  { key: '102', label: '六一组' },
  { key: '101', label: '十万组' },
  { key: '100', label: '百万组' },
  { key: '103', label: '千万组' }
];

const withGroupSuffix = (name: string) => {
  const text = String(name || '').trim();
  if (!text) return '';
  return text.endsWith('组') ? text : `${text}组`;
};

type UmsGameGroupQrcode = {
  id: number;
  gameSession: string;
  groupType: string;
  groupName: string;
  qrCodeUrl: string;
  status: number;
  joinedCount: number;
  maxMemberCount: number;
  isValid: number;
  createTime?: string;
  updateTime?: string;
};

type GameGroupQrcodeListResp = {
  activeCount: number;
  backupCount: number;
  list: UmsGameGroupQrcode[];
};

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 }
};

const toDateTimeText = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return '-';
  return parsed.format('YYYY-MM-DD HH:mm:ss');
};

const getUploadUrl = (file: any) => file?.url || file?.response?.data || '';

function GameGroupQrcodeManagement() {
  const [form] = Form.useForm();
  const [seasonOptions, setSeasonOptions] = useState<{ label: string; value: string }[]>([]);
  const [currentSeason, setCurrentSeason] = useState<string>('');
  const [groupTabs, setGroupTabs] = useState<{ key: string; label: string }[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [backupCount, setBackupCount] = useState(0);
  const [dataList, setDataList] = useState<UmsGameGroupQrcode[]>([]);
  const [visible, setVisible] = useState(false);
  const [qrFileList, setQrFileList] = useState<any[]>([]);
  const [editingRecord, setEditingRecord] = useState<UmsGameGroupQrcode | null>(null);

  // 当前赛季 / 当前分组的展示名称（弹窗只读展示用）
  const currentSeasonLabel = useMemo(
    () => seasonOptions.find(item => item.value === currentSeason)?.label || currentSeason,
    [seasonOptions, currentSeason]
  );
  const currentGroupLabel = useMemo(
    () => groupTabs.find(item => item.key === activeTab)?.label || activeTab,
    [groupTabs, activeTab]
  );

  // 查询指定 赛季+分组 的二维码列表
  const fetchList = useCallback((season: string, groupType: string) => {
    setLoading(true);
    get<GameGroupQrcodeListResp>('/game/groupQrcode/list', { gameSession: season, groupType })
      .then((res: any) => {
        if (res?.code === 200 && res?.data) {
          setActiveCount(res.data.activeCount ?? 0);
          setBackupCount(res.data.backupCount ?? 0);
          setDataList(res.data.list || []);
        } else {
          Message.error(res?.message || '获取列表失败');
        }
      })
      .catch(() => {
        Message.error('获取列表失败');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // 1. 初始化：加载赛季列表（全部有效赛季：报名中未开始/进行中/已结束，排除量化赛）
  useEffect(() => {
    get('/gameConfig/getAllGameSessions')
      .then((res: any) => {
        if (res?.code === 200 && res?.data) {
          const options = (res.data || []).map((item: any) => ({
            label: item.gameSessionName || item.gameSession,
            value: item.gameSession
          }));
          const sortedOptions = options.sort((a: { value: string }, b: { value: string }) => {
            const aVal = String(a.value);
            const bVal = String(b.value);
            const aNum = /^\d+$/.test(aVal);
            const bNum = /^\d+$/.test(bVal);
            if (aNum && bNum) return parseInt(bVal, 10) - parseInt(aVal, 10);
            if (aNum && !bNum) return -1;
            if (!aNum && bNum) return 1;
            return bVal.localeCompare(aVal);
          });
          setSeasonOptions(sortedOptions);
          if (sortedOptions.length > 0) {
            setCurrentSeason(sortedOptions[0].value);
          } else {
            setDataList([]);
            setActiveCount(0);
            setBackupCount(0);
          }
        } else {
          Message.error(res?.message || '获取赛季列表失败');
        }
      })
      .catch(() => {
        Message.error('获取赛季列表失败');
      });
  }, []);

  // 2. 赛季切换：加载该赛季的分组 Tab；无配置时回退默认分组，保证可新增二维码
  useEffect(() => {
    if (!currentSeason) return;
    get('/groupType/getGroupTypeBySession', { gameSession: currentSeason })
      .then((res: any) => {
        if (res?.code === 200 && res?.data) {
          const validGroups = (res.data || []).filter((item: any) => item.isValid === 1);
          const tabs =
            validGroups.length > 0
              ? validGroups.map((item: any) => ({
                  key: String(item.groupType),
                  label: withGroupSuffix(item.groupTypeName || item.groupType)
                }))
              : DEFAULT_GROUP_TABS;
          setGroupTabs(tabs);
          const defaultGroup =
            validGroups.find((item: any) => item.defaultSelected === 1) || validGroups[0];
          setActiveTab(defaultGroup ? String(defaultGroup.groupType) : tabs[0].key);
          if (validGroups.length === 0) {
            setDataList([]);
            setActiveCount(0);
            setBackupCount(0);
          }
        } else {
          Message.error(res?.message || '获取分组列表失败');
          setGroupTabs(DEFAULT_GROUP_TABS);
          setActiveTab(DEFAULT_GROUP_TABS[0].key);
          setDataList([]);
          setActiveCount(0);
          setBackupCount(0);
        }
      })
      .catch(() => {
        Message.error('获取分组列表失败');
        setGroupTabs(DEFAULT_GROUP_TABS);
        setActiveTab(DEFAULT_GROUP_TABS[0].key);
        setDataList([]);
        setActiveCount(0);
        setBackupCount(0);
      });
  }, [currentSeason]);

  // 3. 赛季/分组变化：拉取对应二维码列表
  useEffect(() => {
    if (!currentSeason || !activeTab) return;
    fetchList(currentSeason, activeTab);
  }, [currentSeason, activeTab, fetchList]);

  const resetModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setQrFileList([]);
    setVisible(false);
  };

  const openAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setQrFileList([]);
    form.setFieldsValue({
      maxMemberCount: 200
    });
    setVisible(true);
  };

  const openEditModal = (record: UmsGameGroupQrcode) => {
    setEditingRecord(record);
    form.resetFields();
    setQrFileList(record.qrCodeUrl ? [{ uid: `qr-${record.id}`, url: record.qrCodeUrl, name: 'qrcode' }] : []);
    form.setFieldsValue({
      groupName: record.groupName,
      qrCodeUrl: record.qrCodeUrl,
      maxMemberCount: record.maxMemberCount ?? 200,
      isActive: record.status === STATUS_ACTIVE
    });
    setVisible(true);
  };

  const handleQrChange = (fileList: any[]) => {
    const latest = fileList.slice(-1);
    const latestUrl = getUploadUrl(latest[0]);
    setQrFileList(latest);
    form.setFieldValue('qrCodeUrl', latestUrl || '');
    if (latestUrl) {
      Message.success('上传成功');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validate();
      const payload: Record<string, any> = {
        groupName: values.groupName?.trim(),
        qrCodeUrl: values.qrCodeUrl,
        maxMemberCount: values.maxMemberCount
      };

      setSubmitLoading(true);
      const editing = editingRecord;
      if (editing?.id) {
        await post(`/game/groupQrcode/update/${editing.id}`, payload);
        Message.success('编辑成功');
      } else {
        await post('/game/groupQrcode/add', {
          ...payload,
          gameSession: currentSeason,
          groupType: activeTab
        });
        Message.success('新增成功');
      }
      resetModal();
      fetchList(currentSeason, activeTab);
    } catch (error: any) {
      if (error?.errorFields) {
        Message.error('请完善表单信息');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleActivate = (record: UmsGameGroupQrcode) => {
    if (!record?.id) return;
    setActionLoadingId(record.id);
    post(`/game/groupQrcode/activate/${record.id}`)
      .then((res: any) => {
        if (res?.code === 200) {
          Message.success('已设为生效');
          fetchList(currentSeason, activeTab);
        } else {
          Message.error(res?.message || '设为生效失败');
        }
      })
      .catch(() => {
        Message.error('设为生效失败');
      })
      .finally(() => {
        setActionLoadingId(null);
      });
  };

  const handleDelete = (record: UmsGameGroupQrcode) => {
    if (!record?.id) return;
    if (record.status === STATUS_ACTIVE) {
      Message.warning('生效中的二维码不可删除，请先切换其他二维码为生效');
      return;
    }
    setActionLoadingId(record.id);
    post(`/game/groupQrcode/delete/${record.id}`)
      .then((res: any) => {
        if (res?.code === 200) {
          Message.success('删除成功');
          fetchList(currentSeason, activeTab);
        } else {
          Message.error(res?.message || '删除失败');
        }
      })
      .catch(() => {
        Message.error('删除失败');
      })
      .finally(() => {
        setActionLoadingId(null);
      });
  };

  const renderQrcodeItem = (record: UmsGameGroupQrcode) => {
    const isActive = record.status === STATUS_ACTIVE;
    const itemLoading = actionLoadingId === record.id;

    return (
      <div key={record.id} className={`qrcode-item ${isActive ? 'is-active' : 'is-backup'}`}>
        <div className="qrcode-image-wrap">
          {record.qrCodeUrl ? (
            <Image src={record.qrCodeUrl} width={96} height={96} preview={true} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: '#f2f3f5' }} />
          )}
        </div>

        <div className="qrcode-info">
          <div className="qrcode-title-row">
            <span className="qrcode-title">{record.groupName || '-'}</span>
            <Tag color={isActive ? 'green' : 'gray'}>{isActive ? '使用中' : '已停用'}</Tag>
          </div>
          <div className="qrcode-meta">
            <span>已加入 {record.joinedCount ?? 0} / {record.maxMemberCount ?? 0} 人</span>
            <span>创建时间 {toDateTimeText(record.createTime)}</span>
            <span>修改时间 {toDateTimeText(record.updateTime)}</span>
          </div>
        </div>

        <div className="qrcode-actions">
          {isActive ? (
            <Button type="outline" status="success" disabled icon={<IconCheck />}>
              当前生效
            </Button>
          ) : (
            <Button type="primary" loading={itemLoading} onClick={() => handleActivate(record)}>
              设为生效
            </Button>
          )}
          <Button type="outline" icon={<IconEdit />} loading={itemLoading} onClick={() => openEditModal(record)}>
            编辑
          </Button>
          <Popconfirm
            title="提示"
            content="是否确认删除该二维码？"
            disabled={isActive}
            onOk={() => handleDelete(record)}
          >
            <Button
              type="outline"
              status="danger"
              icon={<IconDelete />}
              loading={itemLoading}
              disabled={isActive}
            >
              删除
            </Button>
          </Popconfirm>
        </div>
      </div>
    );
  };

  return (
    <div className="game-group-qrcode-page">
      <div className="page-header">
        <div className="season-selector">
          <span className="season-label">赛季</span>
          <Select
            value={currentSeason}
            onChange={setCurrentSeason}
            style={{ width: 240 }}
            placeholder="请选择赛季"
          >
            {seasonOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <h2 className="page-title">比赛进群二维码管理</h2>
      </div>

      {groupTabs.length > 0 ? (
        <Tabs
          className="group-tabs"
          activeTab={activeTab}
          onChange={setActiveTab}
          extra={
            <Button type="primary" icon={<IconPlus />} onClick={openAddModal}>
              新增二维码
            </Button>
          }
        >
          {groupTabs.map(tab => (
            <Tabs.TabPane key={tab.key} title={tab.label}>
              <div className="stats-row">
                <div className="stats-card active">
                  <div className="stats-label">当前生效群组</div>
                  <div className="stats-value">{activeCount} 个</div>
                </div>
                <div className="stats-card backup">
                  <div className="stats-label">备用群组</div>
                  <div className="stats-value">{backupCount} 个</div>
                </div>
              </div>

              <Spin loading={loading} style={{ width: '100%' }}>
                <div className="qrcode-list">
                  {dataList.length > 0 ? (
                    dataList.map(renderQrcodeItem)
                  ) : (
                    !loading && <div className="empty-list">暂无二维码数据</div>
                  )}
                </div>
              </Spin>

              <div className="notice-box">
                <div className="notice-title">注意事项</div>
                同一赛季同一分组内，同时仅能有一个二维码生效。切换生效二维码后，旧二维码将立即停止扫描入群功能。请确保备用二维码已准备就绪再进行切换操作，以免影响用户体验。
              </div>
            </Tabs.TabPane>
          ))}
        </Tabs>
      ) : (
        <div className="empty-list">当前赛季暂无分组配置</div>
      )}

      <Modal
        title={editingRecord?.id ? '编辑二维码' : '新增二维码'}
        visible={visible}
        onCancel={resetModal}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        unmountOnExit
        style={{ width: 560 }}
      >
        {!editingRecord?.id && (
          <div className="modal-scope">
            <span>
              赛季：<b>{currentSeasonLabel}</b>
            </span>
            <span>
              分组：<b>{currentGroupLabel}</b>
            </span>
          </div>
        )}
        <Form {...formItemLayout} form={form}>
          <FormItem label="群名" field="groupName" rules={[{ required: true, message: '请输入群名' }]}>
            <Input placeholder="请输入..." maxLength={64} />
          </FormItem>

          <FormItem label="二维码" required>
            <FormItem field="qrCodeUrl" noStyle rules={[{ required: true, message: '请上传二维码' }]}>
              <Input style={{ display: 'none' }} />
            </FormItem>
            <Upload
              listType="picture-card"
              imagePreview
              limit={1}
              accept="image/*"
              action={URL_UPLOAD}
              headers={{ Authorization: `${localStorage.getItem('authToken')}` }}
              fileList={qrFileList}
              onChange={handleQrChange}
            />
          </FormItem>

          {editingRecord ? (
            <FormItem label="是否生效" field="isActive" triggerPropName="checked">
              <Switch disabled checkedText="使用中" uncheckedText="已停用" />
            </FormItem>
          ) : null}

          <FormItem
            label="人数上限"
            field="maxMemberCount"
            rules={[{ required: true, message: '请输入人数上限' }]}
          >
            <InputNumber min={1} precision={0} placeholder="请输入..." style={{ width: '100%' }} />
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
}

export default GameGroupQrcodeManagement;
